import React, { useState, useEffect, useRef } from 'react';
import { useAppStore } from '../store/useAppStore';
import { XMarkIcon, PaperAirplaneIcon, ChatBubbleLeftEllipsisIcon, CheckIcon, ClockIcon, MapPinIcon } from './Icons';
import { api } from '../services/api';

const ChatWindow: React.FC = () => {
  const { 
    seller, 
    activeChatId, 
    activeChatMessages, 
    activeProduct,
    isChatOpen, 
    closeChat, 
    sendChatMessage, 
    chatSessions, 
    fetchChatSessions,
    openChat,
    fetchMessages
  } = useAppStore();
  
  const [text, setText] = useState('');
  const [view, setView] = useState<'sessions' | 'chat'>('sessions');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isChatOpen) {
      fetchChatSessions();
    }
  }, [isChatOpen]);

  useEffect(() => {
    if (activeChatId) {
      setView('chat');
    } else {
      setView('sessions');
    }
  }, [activeChatId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeChatMessages]);

  useEffect(() => {
    const cleanup = api.onChatUpdate((data) => {
      if ((data.type === 'REFRESH' || data.type === 'NEW_MESSAGE' && data.chatId === activeChatId) && activeChatId) {
        fetchMessages(activeChatId);
      }
      if (data.type === 'MESSAGE_SYNCED' && data.chatId === activeChatId && activeChatId) {
        fetchMessages(activeChatId);
      }
      fetchChatSessions();
    });
    return cleanup;
  }, [activeChatId]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() || !activeChatId || !seller) return;
    
    const session = chatSessions.find(s => s.id === activeChatId);
    
    let recipient: string;
    let productId: string;
    let productName: string;

    if (session) {
      recipient = session.sellerContact === seller.contact ? session.buyerContact : session.sellerContact;
      productId = session.productId;
      productName = session.productName;
    } else if (activeProduct) {
      recipient = activeProduct.seller.contact;
      productId = activeProduct.id;
      productName = activeProduct.name;
    } else {
      return;
    }
    
    await sendChatMessage(activeChatId, recipient, text, productId, productName);
    setText('');
    // Refresh sessions to show the new session if it was just created
    fetchChatSessions();
  };

  const getActiveTitle = () => {
    const session = chatSessions.find(s => s.id === activeChatId);
    if (session) return session.productName;
    if (activeProduct) return activeProduct.name;
    return 'Direct Message';
  };

  if (!isChatOpen) return null;

  return (
    <div
      role="dialog"
      aria-label="Chat"
      className="fixed inset-0 sm:inset-auto sm:bottom-4 sm:right-4 z-[100] sm:w-96 sm:h-[600px] bg-card-bg sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-10 duration-300 border border-card-border"
    >
      {/* Header */}
      <div className="bg-brand-green-50 border-b border-brand-green-100 p-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          {view === 'chat' && (
            <button onClick={() => setView('sessions')} aria-label="Back to conversations" className="p-1 text-brand-green hover:bg-brand-green-100 rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </button>
          )}
          <ChatBubbleLeftEllipsisIcon className="w-6 h-6 text-brand-green" />
          <h2 className="font-black tracking-tight text-lg text-card-title truncate max-w-[200px]">
            {view === 'sessions' ? 'AgriConnect Chat' : getActiveTitle()}
          </h2>
        </div>
        <button onClick={closeChat} aria-label="Close chat" className="p-2 text-brand-green hover:bg-brand-green-100 rounded-full transition-colors">
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-grow overflow-hidden flex flex-col bg-app-bg">
        {view === 'sessions' ? (
          <div className="flex-grow overflow-y-auto p-4 space-y-3 custom-scrollbar">
            {chatSessions.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-8 text-app-text-secondary">
                <div className="text-5xl mb-4 opacity-30" aria-hidden="true">💬</div>
                <p className="font-bold">No active conversations yet.</p>
                <p className="text-xs mt-2">Start a chat from a product detail page.</p>
              </div>
            ) : (
              [...chatSessions].sort((a,b) => b.lastTimestamp - a.lastTimestamp).map(session => (
                <button 
                  key={session.id}
                  onClick={() => openChat(session.id)}
                  className={`w-full text-left p-4 rounded-2xl border-2 transition-all flex gap-4 ${activeChatId === session.id ? 'bg-brand-green text-white border-brand-green shadow-card-hover' : 'bg-card-bg border-card-border hover:border-brand-green text-brand-brown shadow-card'}`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-black flex-shrink-0 ${activeChatId === session.id ? 'bg-brand-yellow text-btn-accent-text' : 'bg-brand-brown-50 text-brand-brown'}`}>
                    {session.productName.charAt(0)}
                  </div>
                  <div className="flex-grow min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-black truncate text-sm">{session.productName}</h3>
                      <span className={`text-[10px] font-bold uppercase ${activeChatId === session.id ? 'text-white/80' : 'text-app-text-secondary'}`}>
                        {new Date(session.lastTimestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    </div>
                    <p className={`text-xs truncate ${activeChatId === session.id ? 'text-white/80' : 'text-app-text-secondary'}`}>
                      {session.lastMessage || "No messages yet"}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        ) : (
          <div className="flex-grow flex flex-col overflow-hidden">
            <div ref={scrollRef} aria-live="polite" className="flex-grow overflow-y-auto p-4 space-y-4 custom-scrollbar">
              {activeChatMessages.length === 0 && activeProduct && (
                 <div className="text-center py-10 text-app-text-secondary">
                    <p className="text-xs font-black uppercase tracking-widest">New Chat Started</p>
                    <p className="text-[11px] mt-1">Inquiring about {activeProduct.name}</p>
                 </div>
              )}
              {activeChatMessages.map((msg, i) => {
                const isMe = seller && msg.senderContact === seller.contact;
                return (
                  <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-3 rounded-2xl shadow-card relative ${isMe ? 'bg-brand-green text-white rounded-tr-none' : 'bg-card-bg text-card-text rounded-tl-none border border-card-border'}`}>
                      <p className="text-sm leading-relaxed">{msg.text}</p>
                      <div className="flex items-center justify-end gap-1 mt-1">
                        <span className={`text-[10px] font-bold ${isMe ? 'text-white/80' : 'text-app-text-secondary'}`}>
                          {new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                        {isMe && (
                          msg.status === 'SENDING' ? <ClockIcon className="w-3 h-3 text-white/80 animate-pulse" /> : <CheckIcon className="w-3 h-3 text-brand-yellow" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <form onSubmit={handleSend} className="p-4 bg-card-bg border-t border-divider flex gap-2">
              <input 
                type="text" 
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Type your message..."
                aria-label="Type your message"
                className="flex-grow px-4 py-3 bg-input-bg text-input-text border border-input-border rounded-full hover:border-input-border-hover focus:border-brand-green focus:ring-2 focus:ring-focus-ring outline-none text-sm"
              />
              <button 
                type="submit"
                disabled={!text.trim()}
                aria-label="Send message"
                className="bg-btn-primary text-btn-primary-text p-3 rounded-full hover:bg-btn-primary-hover transition-all shadow-card-hover active:scale-95 disabled:opacity-50"
              >
                <PaperAirplaneIcon className="w-5 h-5" />
              </button>
            </form>
          </div>
        )}
      </div>
      
      <div role="status" className="bg-brand-brown-50 p-2 border-t border-divider text-[10px] text-center font-black text-app-text-secondary uppercase tracking-widest flex items-center justify-center gap-2">
        <div className={`w-1.5 h-1.5 rounded-full ${navigator.onLine ? 'bg-brand-light-green' : 'bg-error-text animate-pulse'}`} />
        {navigator.onLine ? 'Real-time Enabled' : 'Offline Engine Active • Auto-Sync Enabled'}
      </div>
    </div>
  );
};

export default ChatWindow;
